import React, {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {useDealContext} from "../../contexts/DealContext";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import {mileageOptions, termOptions} from "./DealPagePayment";

const msdOptions = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

// Drop the menu directly below the control, left-aligned — same as the Calc page.
const selectMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    marginThreshold: 4,
}

// Editable combobox for Term / Mileage: accepts a custom numeric value
// (freeSolo) AND always lists every preset (filterOptions is identity, so the
// menu never narrows as you type). Commits to dealState on blur / Enter / pick.
const StructureCombo = ({ value, options, onCommit, disabled }) => {
    const [input, setInput] = useState(String(value ?? ''))

    // Re-sync the buffer whenever the committed value changes externally.
    useEffect(() => { setInput(String(value ?? '')) }, [value])

    // Commit a raw buffer string into dealState (no-op if unchanged).
    const commit = (raw) => {
        if (raw !== String(value ?? '')) {
            onCommit(raw === '' ? null : Number(raw))
        }
    }

    return (
        <Autocomplete
            freeSolo
            disableClearable
            disabled={disabled}
            blurOnSelect
            forcePopupIcon={true}
            className={'structure-autocomplete'}
            ListboxProps={{ className: 'structure-autocomplete-listbox' }}
            options={options.map(String)}
            filterOptions={(opts) => opts}        // never filter — show every option
            inputValue={input}
            onInputChange={(e, val) => {
                if (!/^\d*$/.test(val)) return
                setInput(val)
                // Commit live (on select/type) rather than waiting for blur.
                // Pressing Enter or clicking Calculate blurs this field and
                // reads dealState in the SAME tick — if we only committed on
                // blur, that read would still see the old value and the first
                // calculate would re-price with stale mileage/term. Committing
                // as the value changes keeps dealState current before then.
                commit(val)
            }}
            onBlur={() => commit(input)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'—'}
                    inputProps={{ ...params.inputProps, inputMode: 'numeric' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                />
            )}
        />
    )
}

export const DealPageStructure = () => {
    const { dealState, setDealStateWithUpdate, isHistorical } = useDealContext()

    if (!dealState) return null;

    const isPurchase = Boolean(dealState.is_purchase)
    // Cash deal = purchase paid in full up front. Term / down payment / APR
    // don't apply, so those fields read "N/A" instead of an editable value.
    const isCash = isPurchase && Boolean(dealState.is_one_pay)
    const isCapReduction = Boolean(dealState.is_drive_off_cap_reduction)
    const driveOffLabel = isCapReduction ? 'Cap Reduction:' : 'Drive Off:'
    const driveOffTooltip = isCapReduction ? 'Use cash as total drive off' : 'Use cash as cap reduction'

    const termCombo = (
        <StructureCombo
            value={dealState.term}
            options={termOptions}
            disabled={isHistorical}
            onCommit={(v) => setDealStateWithUpdate({term: v})}

        />
    )

    // ── Finance / Purchase variant ──
    // Simpler structure: just Custom Down Payment (no cap-reduction toggle)
    // and Term. No One Pay / MSDs / Mileage for finance deals.
    if (isPurchase) {
        return (
            <Box className={'panel-variables'}>
                <div className={'panel-header'}>
                    <div className={'panel-title'}>Structure</div>
                </div>
                <div className={'panel-body'}>
                    <div className={'pricing-row'}>
                        <span className={'pricing-label'}>Custom Down Payment</span>
                        {
                            isCash
                                ? <span className={'pricing-na'}>N/A</span>
                                : <div className={'pricing-input-wrap'}>
                                    <span className={'currency-prefix'}>$</span>
                                    <CustomCurrencyInput
                                        value={dealState.drive_off}
                                        allowNull={true}
                                        disabled={isHistorical}
                                        onChange={(value) => setDealStateWithUpdate({
                                            drive_off: value,
                                            desired_payment: null,
                                        })}
                            
                                        InputProps={{}}
                                    />
                                </div>
                        }
                    </div>

                    <div className={'select-row'}>
                        <label>Term</label>
                        {isCash ? <span className={'pricing-na'}>N/A</span> : termCombo}
                    </div>

                    <div className={'select-row'}>
                        <label>Days to first payment</label>
                        {isCash
                            ? <span className={'pricing-na'}>N/A</span>
                            : <StructureCombo
                                value={dealState.days_to_first_payment ?? 30}
                                options={[30, 45, 60, 75, 90]}
                                disabled={isHistorical}
                                onCommit={(v) => setDealStateWithUpdate({days_to_first_payment: v})}
                    
                            />
                        }
                    </div>
                </div>
            </Box>
        )
    }

    return (
        <Box className={'panel-variables'}>
            <div className={'panel-header'}>
                <div className={'panel-title'}>Structure</div>
            </div>
            <div className={'panel-body'}>
                {/* Total Drive Off / Cap Reduction */}
                <div className={'toggle-row'}>
                    <span className={'toggle-key'}>{driveOffLabel}</span>
                    <Tooltip title={driveOffTooltip} arrow placement={'top'}>
                        <Switch
                            size={'small'}
                            checked={isCapReduction}
                            disabled={isHistorical}
                            onChange={() => setDealStateWithUpdate({
                                is_drive_off_cap_reduction: !isCapReduction,
                                desired_payment: null,
                            }, true)}
                
                        />
                    </Tooltip>
                    <div className={'toggle-input-wrap'}>
                        <span className={'currency-prefix'}>$</span>
                        <CustomCurrencyInput
                            value={dealState.drive_off}
                            allowNull={true}
                            disabled={isHistorical}
                            onChange={(value) => setDealStateWithUpdate({
                                drive_off: value,
                                desired_payment: null,
                            })}
                
                            InputProps={{}}
                        />
                    </div>
                </div>

                {/* Term */}
                <div className={'select-row'}>
                    <label>Term:</label>
                    {termCombo}
                </div>

                {/* Mileage */}
                <div className={'select-row'}>
                    <label>Mileage:</label>
                    <StructureCombo
                        value={dealState.mileage}
                        options={mileageOptions}
                        disabled={isHistorical}
                        onCommit={(v) => setDealStateWithUpdate({mileage: v})}
            
                    />
                </div>

                {/* One Pay + MSD's share one row, split by a divider */}
                <div className={'toggle-row'}>
                    <span className={'one-pay-label'}>One Pay</span>
                    <Tooltip title={dealState.is_one_pay ? "Switch to monthly payments" : "Single up-front payment for the full term"} arrow placement={'top'}>
                        <Switch
                            size={'small'}
                            checked={Boolean(dealState.is_one_pay)}
                            disabled={isHistorical}
                            onChange={() => setDealStateWithUpdate({is_one_pay: !dealState.is_one_pay}, true)}
                
                        />
                    </Tooltip>
                    <span className={'toggle-divider'}>|</span>
                    <span className={'msd-group'}>
                        <span>MSD's</span>
                        <Select
                            className={'Select structure-select-mui'}
                            disabled={isHistorical}
                            value={String(dealState.msds ?? 0)}
                            MenuProps={{ ...selectMenuProps, MenuListProps: { className: 'structure-select-menu' } }}
                            onChange={(e) => setDealStateWithUpdate({msds: Number(e.target.value)})}
                            value={String(dealState.number_of_security_deposits ?? 0)}
                            MenuProps={{ ...selectMenuProps, MenuListProps: { className: 'structure-select-menu' }, PaperProps: { className: 'structure-select-paper' } }}
                            onChange={(e) => setDealStateWithUpdate({number_of_security_deposits: Number(e.target.value)})}
            
                        >
                            {msdOptions.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                    </span>
                </div>
            </div>
        </Box>
    )
}
